import React, { useState, useEffect, useContext } from 'react';
import { FiEdit2, FiSave } from 'react-icons/fi';
import { FaTimes, FaCheck, FaChevronDown, FaChevronUp, FaPlus, FaLink, FaUnlink, FaSearch, FaChevronLeft, FaChevronRight, FaPalette } from 'react-icons/fa';
import EmojiPicker from 'emoji-picker-react';
import { fetchIdeas, updateIdeaParent, getIdea } from '../../api/ideas';
import { ThemeContext } from '../../context/ThemeContext';
import { themes, THEMES_PER_PAGE, getThemesByPage, getTotalPages } from '../../utils/themes';

const EditModal = ({
  showEditModal,
  setShowEditModal,
  editModalTitle,
  setEditModalTitle,
  editModalDescription,
  setEditModalDescription,
  editModalIcon,
  setEditModalIcon,
  editModalStatus,
  setEditModalStatus,
  editModalTags,
  setEditModalTags,
  editModalNewTag,
  setEditModalNewTag,
  showEditEmojiPicker,
  setShowEditEmojiPicker,
  editingTagIndex,
  setEditingTagIndex,
  editingTagValue,
  setEditingTagValue,
  showTagsSection,
  setShowTagsSection,
  handleEditModalSave,
  handleEditModalAddTag,
  handleEditModalDeleteTag,
  handleEditTag,
  handleSaveTagEdit,
  handleCancelTagEdit,
  // New props for hierarchy
  idea,
  token,
  refreshSidebar
}) => {
  const { theme } = useContext(ThemeContext);
  const [showParentSection, setShowParentSection] = useState(false);
  const [showParentPicker, setShowParentPicker] = useState(false);
  const [parentIdea, setParentIdea] = useState(null);
  const [availableIdeas, setAvailableIdeas] = useState([]);
  const [parentSearchQuery, setParentSearchQuery] = useState('');
  const [isLoadingParent, setIsLoadingParent] = useState(false);
  
  // Theme selection states
  const [showThemeSection, setShowThemeSection] = useState(false);
  const [useCustomTheme, setUseCustomTheme] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [themePage, setThemePage] = useState(0);
  const totalThemePages = getTotalPages();

  // Fetch parent idea details if has parent
  useEffect(() => {
    const fetchParent = async () => {
      if (idea?.parent_id && token) {
        try {
          const parent = await getIdea(idea.parent_id, token);
          setParentIdea(parent);
        } catch (err) {
          console.error('Failed to fetch parent:', err);
        }
      } else {
        setParentIdea(null);
      }
    };
    
    if (showEditModal) {
      fetchParent();
      // Initialize theme states
      if (idea?.custom_theme) {
        setUseCustomTheme(true);
        setSelectedTheme(idea.custom_theme);
      } else {
        setUseCustomTheme(false);
        setSelectedTheme(null);
      }
    }
  }, [idea?.parent_id, idea?.custom_theme, token, showEditModal]);

  // Fetch available ideas for parent picker
  useEffect(() => {
    const fetchAvailableIdeas = async () => {
      if (showParentPicker && token) {
        try {
          const ideas = await fetchIdeas(token);
          // Filter out current idea and its descendants
          const filtered = ideas.filter(i => i.id !== idea?.id);
          setAvailableIdeas(filtered);
        } catch (err) {
          console.error('Failed to fetch ideas:', err);
        }
      }
    };
    
    fetchAvailableIdeas();
  }, [showParentPicker, token, idea?.id]);

  const handleDetachFromParent = async () => {
    if (!idea?.id || !token) return;
    
    try {
      setIsLoadingParent(true);
      await updateIdeaParent(idea.id, null, token);
      setParentIdea(null);
      refreshSidebar?.();
      // Force reload the page to reflect changes
      window.location.reload();
    } catch (err) {
      console.error('Failed to detach from parent:', err);
    } finally {
      setIsLoadingParent(false);
    }
  };

  const handleAttachToParent = async (newParentId) => {
    if (!idea?.id || !token || !newParentId) return;
    
    try {
      setIsLoadingParent(true);
      await updateIdeaParent(idea.id, newParentId, token);
      const parent = await getIdea(newParentId, token);
      setParentIdea(parent);
      setShowParentPicker(false);
      refreshSidebar?.();
      // Force reload the page to reflect changes
      window.location.reload();
    } catch (err) {
      console.error('Failed to attach to parent:', err);
    } finally {
      setIsLoadingParent(false);
    }
  };

  const filteredAvailableIdeas = availableIdeas.filter(i => 
    i.title.toLowerCase().includes(parentSearchQuery.toLowerCase())
  );

  const displayedThemes = getThemesByPage(themePage);

  const goToPrevThemePage = () => {
    setThemePage((prev) => (prev === 0 ? totalThemePages - 1 : prev - 1));
  };

  const goToNextThemePage = () => {
    setThemePage((prev) => (prev === totalThemePages - 1 ? 0 : prev + 1));
  };

  const handleThemeSelect = (themeName) => {
    setSelectedTheme(themeName);
  };

  const handleSaveWithTheme = () => {
    // Pass the theme data to the save handler
    const themeData = useCustomTheme ? selectedTheme : null;
    handleEditModalSave(themeData);
  };

  if (!showEditModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
      <div className="bg-[var(--bg-primary)] rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-[var(--primary)]/10 flex items-center justify-center">
              <FiEdit2 className="text-xl text-[var(--primary)]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[var(--text-primary)]">Edit Idea</h3>
              <p className="text-sm text-[var(--text-secondary)]">Update your idea details</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Icon and Title */}
            <div className="flex items-center gap-4">
              <div className="relative">
                <button
                  className="text-4xl p-3 rounded-lg hover:bg-[var(--bg-hover)] transition"
                  onClick={() => setShowEditEmojiPicker(!showEditEmojiPicker)}
                >
                  {editModalIcon}
                </button>
                {showEditEmojiPicker && (
                  <div className="absolute z-10 mt-2">
                    <EmojiPicker
                      onEmojiClick={(emojiData) => {
                        setEditModalIcon(emojiData.emoji);
                        setShowEditEmojiPicker(false);
                      }}
                    />
                  </div>
                )}
              </div>
              <input
                type="text"
                className="flex-1 text-xl font-bold p-3 border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] placeholder-[var(--text-secondary)]"
                value={editModalTitle}
                onChange={(e) => setEditModalTitle(e.target.value)}
                placeholder="Idea title"
              />
            </div>

            {/* Description and Status Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Description */}
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">Description</label>
                <textarea
                  className="w-full p-4 border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] placeholder-[var(--text-secondary)] text-base leading-relaxed"
                  value={editModalDescription}
                  onChange={(e) => setEditModalDescription(e.target.value)}
                  rows="6"
                  placeholder="Describe your idea in detail..."
                  style={{ fontSize: '16px' }}
                />
              </div>

              {/* Status */}
              <div className="lg:col-span-1">
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">Status</label>
                <select
                  className="w-full p-3 border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                  value={editModalStatus}
                  onChange={(e) => setEditModalStatus(e.target.value)}
                >
                  <option value="draft">Draft</option>
                  <option value="in-progress">In Progress</option>
                  <option value="review">Under Review</option>
                  <option value="completed">Completed</option>
                  <option value="on-hold">On Hold</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            {/* Parent Relationship Section */}
            <div className="border-t border-[var(--border-color)] pt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="flex items-center gap-2 text-sm font-medium text-[var(--text-secondary)]">
                  <FaLink className="w-3 h-3" />
                  Page Hierarchy
                </label>
                <button
                  type="button"
                  onClick={() => setShowParentSection(!showParentSection)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg transition-all duration-200"
                >
                  {showParentSection ? (
                    <>
                      <FaChevronUp className="w-3 h-3" />
                      <span>Hide</span>
                    </>
                  ) : (
                    <>
                      <FaChevronDown className="w-3 h-3" />
                      <span>{parentIdea ? 'Child of: ' + parentIdea.title : 'Standalone Idea'}</span>
                    </>
                  )}
                </button>
              </div>

              {showParentSection && (
                <div className="space-y-3 p-4 bg-[var(--bg-secondary)] rounded-lg">
                  {parentIdea ? (
                    // Has parent - show detach option
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-sm text-[var(--text-secondary)]">This is a sub-page of:</span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-[var(--bg-hover)] rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{parentIdea.icon || '😊'}</span>
                          <span className="text-[var(--text-primary)] font-medium">{parentIdea.title}</span>
                        </div>
                        <button
                          onClick={handleDetachFromParent}
                          disabled={isLoadingParent}
                          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-[var(--bg-secondary)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--warning)]/20 hover:text-[var(--warning)] transition-all duration-200 border border-[var(--border-color)]"
                        >
                          <FaUnlink className="w-3 h-3" />
                          {isLoadingParent ? 'Detaching...' : 'Make Standalone'}
                        </button>
                      </div>
                      <p className="text-xs text-[var(--text-secondary)] mt-2">
                        Detaching will make this idea a standalone idea again.
                      </p>
                    </div>
                  ) : (
                    // No parent - show attach option
                    <div>
                      <p className="text-sm text-[var(--text-secondary)] mb-3">
                        This is a standalone idea. You can make it a sub-page of another idea.
                      </p>
                      
                      {!showParentPicker ? (
                        <button
                          onClick={() => setShowParentPicker(true)}
                          className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-all duration-200"
                        >
                          <FaLink className="w-3 h-3" />
                          Make Sub-page of...
                        </button>
                      ) : (
                        <div className="space-y-3">
                          {/* Search input */}
                          <div className="relative">
                            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--text-secondary)] w-3 h-3" />
                            <input
                              type="text"
                              placeholder="Search ideas..."
                              value={parentSearchQuery}
                              onChange={(e) => setParentSearchQuery(e.target.value)}
                              className="w-full pl-9 pr-4 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                              style={{ fontSize: '16px' }}
                            />
                          </div>

                          {/* Ideas list */}
                          <div className="max-h-48 overflow-y-auto border border-[var(--border-color)] rounded-lg">
                            {filteredAvailableIdeas.length > 0 ? (
                              filteredAvailableIdeas.map(availableIdea => (
                                <button
                                  key={availableIdea.id}
                                  onClick={() => handleAttachToParent(availableIdea.id)}
                                  disabled={isLoadingParent}
                                  className="w-full flex items-center gap-2 p-3 hover:bg-[var(--bg-hover)] transition-colors text-left border-b border-[var(--border-color)] last:border-b-0"
                                >
                                  <span className="text-lg">{availableIdea.icon || '😊'}</span>
                                  <span className="text-[var(--text-primary)] flex-1 truncate">{availableIdea.title}</span>
                                </button>
                              ))
                            ) : (
                              <p className="p-3 text-sm text-[var(--text-secondary)] text-center">
                                No matching ideas found
                              </p>
                            )}
                          </div>

                          <button
                            onClick={() => {
                              setShowParentPicker(false);
                              setParentSearchQuery('');
                            }}
                            className="text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Tags - Collapsible Section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-[var(--text-secondary)]">
                  Tags {editModalTags.length > 0 && (
                    <span className="ml-1 px-2 py-0.5 bg-[var(--primary)]/20 text-[var(--primary)] rounded-full text-xs">
                      {editModalTags.length}
                    </span>
                  )}
                </label>
                <button
                  type="button"
                  onClick={() => setShowTagsSection(!showTagsSection)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg transition-all duration-200 touch-manipulation"
                >
                  {showTagsSection ? (
                    <>
                      <FaChevronUp className="w-3 h-3" />
                      <span>Hide Tags</span>
                    </>
                  ) : (
                    <>
                      <FaChevronDown className="w-3 h-3" />
                      <span>View & Edit Tags</span>
                    </>
                  )}
                </button>
              </div>
              
              {/* Add Tag Input - Always Visible */}
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  className="flex-1 p-3 border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] placeholder-[var(--text-secondary)]"
                  value={editModalNewTag}
                  onChange={(e) => setEditModalNewTag(e.target.value)}
                  onKeyDown={handleEditModalAddTag}
                  placeholder="Add a tag (press Enter)"
                  style={{ fontSize: '16px' }}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (editModalNewTag.trim() && !editModalTags.includes(editModalNewTag.trim())) {
                      setEditModalTags([...editModalTags, editModalNewTag.trim()]);
                      setEditModalNewTag('');
                    }
                  }}
                  className="px-4 py-3 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-all duration-200 hover:scale-105 text-sm touch-manipulation min-w-[50px]"
                  disabled={!editModalNewTag.trim() || editModalTags.includes(editModalNewTag.trim())}
                >
                  <FaPlus />
                </button>
              </div>
              
              {/* Collapsible Tags Content */}
              <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
                showTagsSection ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}>
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2 mb-3 p-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] min-h-[40px]">
                    {editModalTags.map((tag, index) => (
                      <div key={index} className="inline-flex">
                        {editingTagIndex === index ? (
                          <div className="flex items-center gap-2 px-3 py-2 bg-[var(--primary)]/10 border border-[var(--primary)]/50 rounded-lg min-w-[120px]">
                            <input
                              type="text"
                              value={editingTagValue}
                              onChange={(e) => setEditingTagValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleSaveTagEdit(index);
                                } else if (e.key === 'Escape') {
                                  handleCancelTagEdit();
                                }
                              }}
                              className="text-sm bg-transparent border-none outline-none text-[var(--text-primary)] flex-1 min-w-0"
                              style={{ fontSize: '16px' }}
                              autoFocus
                            />
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                onClick={() => handleSaveTagEdit(index)}
                                className="p-1 text-[var(--primary)] hover:text-[var(--primary-dark)] transition-colors touch-manipulation"
                                title="Save"
                              >
                                <FaCheck className="w-4 h-4" />
                              </button>
                              <button
                                onClick={handleCancelTagEdit}
                                className="p-1 text-[var(--text-secondary)] hover:text-[var(--danger)] transition-colors touch-manipulation"
                                title="Cancel"
                              >
                                <FaTimes className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 px-3 py-2 bg-[var(--bg-hover)] text-[var(--text-primary)] border border-[var(--border-color)] rounded-lg text-sm whitespace-nowrap touch-manipulation">
                            <span
                              onClick={() => handleEditTag(index, tag)}
                              className="flex-1 cursor-pointer"
                              title="Tap to edit"
                            >
                              {tag}
                            </span>
                            <button
                              onClick={() => handleEditModalDeleteTag(tag)}
                              className="p-1 text-[var(--danger)] hover:text-[var(--danger-dark)] transition-all duration-200 touch-manipulation ml-1"
                              title="Remove tag"
                            >
                              <FaTimes className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                    {editModalTags.length === 0 && (
                      <span className="text-sm text-[var(--text-secondary)] italic">No tags added yet</span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Quick Tag Preview (when collapsed) */}
              {!showTagsSection && editModalTags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {editModalTags.slice(0, 3).map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-[var(--bg-hover)] text-[var(--text-primary)] rounded text-xs border border-[var(--border-color)]"
                    >
                      {tag}
                    </span>
                  ))}
                  {editModalTags.length > 3 && (
                    <span className="px-2 py-1 text-[var(--text-secondary)] text-xs">
                      +{editModalTags.length - 3} more
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Custom Theme Section */}
          <div className="border-t border-[var(--border-color)] pt-4">
            <div className="flex items-center justify-between mb-2">
              <label className="flex items-center gap-2 text-sm font-medium text-[var(--text-secondary)]">
                <FaPalette className="w-4 h-4" /> Custom Theme
              </label>
              <button
                type="button"
                onClick={() => setShowThemeSection(!showThemeSection)}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg transition-all duration-200"
              >
                {showThemeSection ? (
                  <>
                    <FaChevronUp className="w-3 h-3" />
                    <span>Hide</span>
                  </>
                ) : (
                  <>
                    <FaChevronDown className="w-3 h-3" />
                    <span>{useCustomTheme && selectedTheme ? `Using: ${themes.find(t => t.name === selectedTheme)?.label}` : 'No Custom Theme'}</span>
                  </>
                )}
              </button>
            </div>

            {showThemeSection && (
              <div className="space-y-4 p-4 bg-[var(--bg-secondary)] rounded-lg">
                {/* Use Custom Theme Toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[var(--text-primary)]">Use custom idea theme</span>
                  <button
                    onClick={() => {
                      setUseCustomTheme(!useCustomTheme);
                      if (useCustomTheme) {
                        setSelectedTheme(null);
                      }
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full border border-[var(--text-primary)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:ring-offset-2 ${
                      useCustomTheme ? 'bg-[var(--primary)]' : 'bg-[var(--bg-primary)]'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-[var(--text-primary)] transition-transform ${
                        useCustomTheme ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Theme Selector */}
                {useCustomTheme && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-medium text-[var(--text-secondary)]">
                        Select Theme {selectedTheme && `(${themes.find(t => t.name === selectedTheme)?.label})`}
                      </h4>
                      <span className="text-xs text-[var(--text-secondary)]">
                        {themePage + 1} / {totalThemePages}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {/* Previous Button */}
                      <button
                        type="button"
                        onClick={goToPrevThemePage}
                        className="flex-shrink-0 p-2 rounded-lg bg-[var(--bg-primary)] hover:bg-[var(--bg-hover)] text-[var(--text-primary)] transition-colors"
                        title="Previous page"
                      >
                        <FaChevronLeft className="w-3 h-3" />
                      </button>

                      {/* Theme Grid */}
                      <div className="grid grid-cols-5 gap-2 flex-1">
                        {displayedThemes.map((t) => {
                          const colors = theme === 'dark' ? t.dark : t.light;
                          return (
                            <button
                              type="button"
                              key={t.name}
                              onClick={() => handleThemeSelect(t.name)}
                              className={`h-10 w-full rounded-lg flex items-center justify-center transition-all hover:scale-105 ${
                                selectedTheme === t.name 
                                  ? 'ring-2 ring-offset-2 ring-[var(--primary)]' 
                                  : 'opacity-70 hover:opacity-100'
                              }`}
                              style={{
                                background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`
                              }}
                              title={t.label}
                            >
                              {selectedTheme === t.name && (
                                <span className="text-white text-sm font-bold drop-shadow-md">
                                  ✓
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {/* Next Button */}
                      <button
                        type="button"
                        onClick={goToNextThemePage}
                        className="flex-shrink-0 p-2 rounded-lg bg-[var(--bg-primary)] hover:bg-[var(--bg-hover)] text-[var(--text-primary)] transition-colors"
                        title="Next page"
                      >
                        <FaChevronRight className="w-3 h-3" />
                      </button>
                    </div>

                    <p className="text-xs text-[var(--text-secondary)] mt-3">
                      When "Use Idea Theme" is enabled in the theme menu, this custom theme will be applied when viewing this idea.
                    </p>
                  </div>
                )}

                {!useCustomTheme && (
                  <p className="text-sm text-[var(--text-secondary)] text-center py-3">
                    Enable custom theme to choose a specific theme for this idea
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => setShowEditModal(false)}
              className="px-4 py-2 rounded-lg bg-[var(--bg-secondary)] text-[var(--text-primary)] border border-[var(--border-color)] hover:bg-[var(--bg-hover)] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveWithTheme}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-all duration-200 hover:scale-105"
            >
              <FiSave /> Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditModal;
